import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import LiveCaller from '@/LiveCaller'

/**
 * Centralized WebSocket Store
 *
 * Manages ALL global WebSocket channel subscriptions.
 * Initializes once on login, cleans up on logout.
 * Components subscribe to events via LiveCaller.$on('ws:EventName')
 *
 * Events emitted:
 * - ws:UserAttachedToConversation (user channel)
 * - ws:UserAttachedToConversation:account (account channel)
 * - ws:ConversationCreated
 * - ws:ConversationClosed
 * - ws:ConversationReopened
 * - ws:ParticipantDetached
 * - ws:ConversationMessageCreated
 * - ws:ServeRequestReceived
 * - ws:ServedRequestsDecremented
 * - ws:VisitorUpdated
 * - ws:ChatBotAttached
 * - ws:ChatBotTransferred
 * - ws:AiAssistantAttached
 * - ws:AiAssistantTransferred
 * - ws:AiSourceProcessed (user channel — a vector-store source import finished)
 */
export const useWebSocketStore = defineStore('websocket', () => {
    const initialized = ref(false)
    const accountChannel = ref(null)
    const userChannel = ref(null)
    const skillChannel = ref([])
    const userId = ref(null)
    const accountId = ref(null)

    const isConnected = computed(() => initialized.value)

    /**
     * Bind conversation lifecycle listeners to a channel.
     * Emits the standard ws:* events so all consumers work the same way
     * regardless of whether events come from account or skill channels.
     */
    const bindConversationListeners = (channel) => {
        // ConversationCreated is the single "conversation is open" broadcast
        // (creations AND reopens — there is no separate reopened event);
        // is_new tells them apart. Kept as two internal bus names so
        // downstream consumers (dashboard stages, inbox lists) are unchanged.
        channel.listen('Conversation\\ConversationCreated', (e) => {
            LiveCaller.$emit(e.is_new === false ? 'ws:ConversationReopened' : 'ws:ConversationCreated', e)
        })
        channel.listen('Conversation\\ConversationClosed', (e) => {
            LiveCaller.$emit('ws:ConversationClosed', e)
        })
        channel.listen('UserAttachedToConversation', (e) => {
            LiveCaller.$emit('ws:UserAttachedToConversation:account', e)
        })
        channel.listen('Conversation\\ParticipantDetachedFromConversation', (e) => {
            LiveCaller.$emit('ws:ParticipantDetached', e)
        })
        channel.listen('Conversation\\ConversationMessageCreated', (e) => {
            LiveCaller.$emit('ws:ConversationMessageCreated', e)
        })
        channel.listen('Conversation\\ConversationMessageStatusUpdated', (e) => {
            LiveCaller.$emit('ws:ConversationMessageStatusUpdated', e)
        })
        channel.listen('ChatBot\\ChatBotAttachedToConversation', (e) => {
            LiveCaller.$emit('ws:ChatBotAttached', e)
        })
        channel.listen('ChatBot\\ChatBotTransferredToUser', (e) => {
            LiveCaller.$emit('ws:ChatBotTransferred', e)
        })
        channel.listen('AI\\AiAssistantAttachedToConversation', (e) => {
            LiveCaller.$emit('ws:AiAssistantAttached', e)
        })
        channel.listen('AI\\AiAssistantTransferredToUser', (e) => {
            LiveCaller.$emit('ws:AiAssistantTransferred', e)
        })
    }

    /**
     * Account channel - always subscribed.
     * Conversation lifecycle listeners are always bound here since not all
     * events are broadcast on skill channels yet. Skill channels are additive.
     */
    const setupAccountChannel = () => {
        accountChannel.value = LiveCaller.echo.private(`account.${accountId.value}`)

        bindConversationListeners(accountChannel.value)

        // RTC / Web Call lifecycle (LiveKit). RTCSessionCreated drives the
        // incoming-call ring (replaces the SIP INVITE); the rest keep the
        // user call UI in sync.
        accountChannel.value.listen('RTC\\RTCSessionCreated', (e) => {
            LiveCaller.$emit('ws:RTCSessionCreated', e)
        })
        accountChannel.value.listen('RTC\\RTCSessionDeclined', (e) => {
            LiveCaller.$emit('ws:RTCSessionDeclined', e)
        })
        accountChannel.value.listen('RTC\\RTCSessionJoined', (e) => {
            LiveCaller.$emit('ws:RTCSessionJoined', e)
        })
        accountChannel.value.listen('RTC\\RTCSessionLeft', (e) => {
            LiveCaller.$emit('ws:RTCSessionLeft', e)
        })
        accountChannel.value.listen('RTC\\RTCMediaToggled', (e) => {
            LiveCaller.$emit('ws:RTCMediaToggled', e)
        })

        // Non-conversation events
        accountChannel.value.listen('VisitorUpdated', (e) => {
            LiveCaller.$emit('ws:VisitorUpdated', e)
        })
        // NOTE: lc:member_updated is broadcast on presence-online-users.*,
        // not this private account channel — it's handled (and the own-user
        // availability synced) in stores/onlineUsers.js.
        accountChannel.value.listen('PBX\\DeviceStateChanged', (e) => {
            LiveCaller.$emit('ws:DeviceStateChanged', e)
        })
    }

    /**
     * Skill-based channel subscriptions (from user.skills in /v1/user response).
     * Subscribes to:
     * - account.{accountId}.channel.{channelName} for each channel skill
     * - channel-line.{channelLineId} for each channel line skill
     *
     * The backend ensures no overlap between channels and channel lines.
     */
    const setupSkillChannels = (skills) => {
        const channels = skills?.channels || []
        const channelLines = skills?.channel_lines || []

        channels.forEach((ch) => {
            const name = `account.${accountId.value}.channel.${ch.name}`
            const channel = LiveCaller.echo.private(name)
            bindConversationListeners(channel)
            skillChannel.value.push(name)
        })

        channelLines.forEach((line) => {
            const name = `channel-line.${line.id}`
            const channel = LiveCaller.echo.private(name)
            bindConversationListeners(channel)
            skillChannel.value.push(name)
        })
    }

    /**
     * User channel - user-specific events
     */
    const setupUserChannel = () => {
        userChannel.value = LiveCaller.echo.private(`users.${userId.value}`)

        // User attached to conversation (user-specific)
        userChannel.value.subscription.bind('App\\Events\\UserAttachedToConversation', (data) => {
            LiveCaller.$emit('ws:UserAttachedToConversation', data)
        })

        // Serve requests
        userChannel.value.listen('Conversation\\ConversationServeRequestReceived', (e) => {
            LiveCaller.$emit('ws:ServeRequestReceived', e)
        })

        userChannel.value.listen('Conversation\\ServedRequestsDecremented', (e) => {
            LiveCaller.$emit('ws:ServedRequestsDecremented', e)
        })

        // AI events
        userChannel.value.listen('AI\\AIMessageSuggestion', (e) => {
            LiveCaller.$emit('ws:AIMessageSuggestion', e)
        })
        userChannel.value.listen('AI\\AiSourceProcessed', (e) => {
            LiveCaller.$emit('ws:AiSourceProcessed', e)
        })

        // Export notifications - download URL when an export is ready, or that it failed
        userChannel.value.notification((notification) => {
            if (notification?.type === 'App\\Notifications\\SendExportCompletedNotification') {
                LiveCaller.$emit('ws:ExportCompleted', notification)
            } else if (notification?.type === 'App\\Notifications\\SendExportFailedNotification') {
                LiveCaller.$emit('ws:ExportFailed', notification)
            }
        })
    }

    /**
     * Initialize WebSocket channels - call once on login
     */
    const initialize = (user) => {
        if (initialized.value) {
            return
        }

        if (!user?.id || !user?.account_id) {
            return
        }

        userId.value = user.id
        accountId.value = user.account_id

        const { skills } = user

        setupAccountChannel()
        if (skills?.channels?.length || skills?.channel_lines?.length) {
            setupSkillChannels(skills)
        }
        setupUserChannel()

        initialized.value = true
    }

    /**
     * Cleanup - call on logout
     */
    const destroy = () => {
        if (!initialized.value) return

        if (accountChannel.value) {
            LiveCaller.echo.leave(`account.${accountId.value}`)
            accountChannel.value = null
        }

        skillChannel.value.forEach((name) => {
            LiveCaller.echo.leave(name)
        })
        skillChannel.value = []

        if (userChannel.value) {
            LiveCaller.echo.leave(`users.${userId.value}`)
            userChannel.value = null
        }

        userId.value = null
        accountId.value = null
        initialized.value = false
    }

    return {
        // State
        initialized,
        isConnected,
        userId,
        accountId,

        // Actions
        initialize,
        destroy,
    }
})

export default useWebSocketStore

